import { EPlaybackRateMode } from '@bufferController/PlaybackSpeedController/IPlaybackSpeedController';
import { IPlaybackSpeedActuatorConfig } from '@bufferController/PlaybackSpeedController/PlaybackSpeedActuator/PlaybackSpeedActuator.config';
import { PlaybackSpeedEvents } from '@bufferController/PlaybackSpeedController/TargetBuffer.events';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { EDebuggableModuleState, IDebbugable, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import bind from '@utils/decorators/bind';

export class PlaybackSpeedActuator extends ConfigConsumer<IPlaybackSpeedActuatorConfig> implements IDebbugable {
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public groups: string[] = ['PlaybackSpeed'];
  private _videoElement: HTMLVideoElement | null = null;
  private _currentPlaybackSpeed: EPlaybackRateMode = EPlaybackRateMode.NORMAL;
  private readonly _onPlaybackSpeedChangedListener: string;

  constructor(eventBus: TEventBus) {
    super('playbackSpeedActuator', eventBus);

    this._onPlaybackSpeedChangedListener = this._eventBus.subscribe(PlaybackSpeedEvents.setPlaybackSpeed, this._onPlaybackSpeedChanged);
  }

  @bind
  destroy(): void {
    super.destroy();

    this._eventBus.unsubscribe(this._onPlaybackSpeedChangedListener);
  }

  public setVideoElement(videoElement: HTMLVideoElement | null): void {
    this._videoElement = videoElement;

    this._setPlaybackSpeed();
  }

  @bind
  public _handleConfigUpdated(): void {
    this._setPlaybackSpeed();
  }

  @bind
  private _onPlaybackSpeedChanged(rateMode: EPlaybackRateMode): void {
    this._currentPlaybackSpeed = rateMode;

    this._setPlaybackSpeed();
  }

  private _setPlaybackSpeed(): void {
    if (!this._videoElement) {
      return;
    }

    const modeToPlaybackRateMap: Record<EPlaybackRateMode, number> = {
      [EPlaybackRateMode.FAST]: this.config.maxPlaybackRate,
      [EPlaybackRateMode.NORMAL]: 1,
      [EPlaybackRateMode.SLOW]: this.config.minPlaybackRate,
    };

    this._videoElement.playbackRate = modeToPlaybackRateMap[this._currentPlaybackSpeed];
  }

  get additionalInfo(): TDebuggableAdditionalInfo {
    return {
      currentPlaybackMode: this._currentPlaybackSpeed,
    };
  }
}
